import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api, { inr } from "../../lib/api";

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const k = data.kpis;
  const series = data.series.labels.map((l, i) => ({ day: l, revenue: data.series.spend[i], cost: data.series.cost[i], profit: data.series.profit[i] }));

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Platform Analytics</h1>
        <p className="text-sm text-slate-500">Revenue, cost, profit and network performance — last 30 days.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          ["Revenue", inr(k.revenue), "text-shipany-blue"], ["Provider Cost", inr(k.cost), "text-slate-700"],
          ["Gross Profit", inr(k.profit), "text-emerald-600"], ["Margin", k.margin_pct + "%", "text-emerald-600"],
          ["NDR Rate", k.ndr_rate + "%", "text-orange-600"], ["RTO Rate", k.rto_rate + "%", "text-red-500"],
          ["COD Collected", inr(k.cod_collected), "text-amber-600"], ["COD Settled", inr(k.cod_settled), "text-slate-700"],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`aan-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
            <p className={`mono mt-2 text-xl font-bold ${cls}`}>{val}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-revenue-chart">
        <h2 className="font-display text-sm font-bold text-shipany-navy">Revenue vs Cost vs Profit</h2>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series}>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={6} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" />
              <Tooltip formatter={(v) => inr(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#0757D9" strokeWidth={2} fill="#0757D9" fillOpacity={0.15} />
              <Area type="monotone" dataKey="cost" stroke="#94A3B8" strokeWidth={2} fill="#94A3B8" fillOpacity={0.1} />
              <Area type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2} fill="#10B981" fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="provider-split-table">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Provider Split</h2>
          <table className="mt-3 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th className="py-2">Provider</th><th>Shipments</th><th>Revenue</th><th>Cost</th><th>Profit</th></tr></thead>
            <tbody>
              {data.provider_split.map((p) => (
                <tr key={p.provider} className="border-t border-slate-100">
                  <td className="py-2.5 font-semibold capitalize text-shipany-navy">{p.provider}</td>
                  <td className="mono">{p.shipments}</td>
                  <td className="mono">{inr(p.revenue)}</td>
                  <td className="mono text-slate-500">{inr(p.cost)}</td>
                  <td className={`mono font-bold ${p.revenue - p.cost >= 0 ? "text-emerald-600" : "text-red-500"}`}>{inr(p.revenue - p.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="courier-perf-mini">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Courier Leaderboard</h2>
          <table className="mt-3 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th className="py-2">Courier</th><th>Score</th><th>Delivery %</th><th>NDR %</th></tr></thead>
            <tbody>
              {data.courier_performance.slice(0, 8).map((c) => (
                <tr key={c.courier_code} className="border-t border-slate-100">
                  <td className="py-2.5 font-medium text-shipany-navy">{c.courier_name}</td>
                  <td className="mono font-bold text-shipany-blue">{c.score}</td>
                  <td className="mono text-emerald-600">{c.delivery_success}%</td>
                  <td className="mono text-orange-600">{c.ndr_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
